import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { PurchaseHeaderActionButton } from './PurchaseHeaderActionButton';
import {
    PURCHASE_HEADER_ACTIONS,
    type PurchaseHeaderActionKey,
} from './purchaseHeaderMeta';

type PurchaseHeaderViewProps = {
    onAction: (key: PurchaseHeaderActionKey) => void;
    /** True while the Excel template is being generated/downloaded. */
    isGeneratingTemplate: boolean;
    /** True when the last template build failed — see `usePurchaseHeader`. */
    templateFailed: boolean;
    onDismissTemplateError: () => void;
};

/**
 * Right-aligned button row that sits at the top of the Purchases page.
 * The actual page title comes from the layout (`getNavigationTitle`),
 * so this section only carries the actions — no heading, no padding
 * (the layout already pads the main content area).
 *
 * On narrow viewports the row wraps and re-aligns to the leading edge so
 * the buttons stay tappable without forcing horizontal scroll.
 */
export const PurchaseHeaderView: React.FC<PurchaseHeaderViewProps> = ({
    onAction,
    isGeneratingTemplate,
    templateFailed,
    onDismissTemplateError,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            ariaLabel: t('purchases.actions.ariaLabel'),
            templateError: t('purchases.actions.templateError'),
            dismiss: t('purchases.actions.templateErrorDismiss'),
        }),
        [t],
    );

    return (
        <section
            aria-label={copy.ariaLabel}
            className="flex flex-wrap items-center justify-start gap-2 sm:justify-end"
            {...getNodeTestIdProp('purchase-header-section')}
        >
            {/* The template build can fail (a slow or refused catalog call).
                It used to fail into the console: the spinner stopped, no file
                arrived, and the row said nothing — indistinguishable from a
                dead button. `role="alert"` so it is announced, and dismissible
                because the next press is the retry. */}
            {templateFailed ? (
                <div
                    className="flex items-center gap-2 rounded-xl bg-danger-soft px-3 py-2 text-sm text-danger-soft-foreground"
                    role="alert"
                    {...getNodeTestIdProp('purchase-header-template-error')}
                >
                    <span>{copy.templateError}</span>
                    <Button
                        onPress={onDismissTemplateError}
                        size="sm"
                        variant="tertiary"
                        {...getNodeTestIdProp(
                            'purchase-header-template-error-dismiss',
                        )}
                    >
                        {copy.dismiss}
                    </Button>
                </div>
            ) : null}
            {PURCHASE_HEADER_ACTIONS.map((action) => (
                <PurchaseHeaderActionButton
                    action={action}
                    isLoading={
                        isGeneratingTemplate && action.slug === 'excelTemplate'
                    }
                    key={action.slug}
                    onAction={onAction}
                />
            ))}
        </section>
    );
};
